import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import Card from '../ui/Card';
import {
  Coins, ChevronLeft, ChevronRight, Loader2, Users, Hash,
  AlertCircle, Inbox, Mic, Languages, Captions, ExternalLink,
  Sparkles, BrainCircuit,
  type LucideIcon,
} from 'lucide-react';

// ── Types (mirrors UsageService.ts on the backend) ──────────────────────────

interface SarvamToolBreakdown {
  toolId: string;
  metricType: 'stt_seconds' | 'translate_chars';
  calls: number;
  totalMetricValue: number;
  creditsUsed: number;
}

interface OpenAiToolBreakdown {
  toolId: string;
  model: string | null;
  metricType: 'openai_tokens' | 'openai_audio_seconds';
  calls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalAudioSeconds: number;
  costUsd: number;
}

interface UserUsageReport {
  username: string;
  sarvamTools: SarvamToolBreakdown[];
  totalSarvamCredits: number;
  openaiTools: OpenAiToolBreakdown[];
  totalOpenAiCostUsd: number;
  totalCalls: number;
}

interface UsageReportResponse {
  from: string;
  to: string;
  users: UserUsageReport[];
  grandTotalSarvamCredits: number;
  grandTotalOpenAiCostUsd: number;
  pricing: {
    sarvam: { sttInrPerHour: number; translateInrPer10kChars: number; source: string };
    openai: { source: string };
  };
}

// ── Formatting helpers ───────────────────────────────────────────────────────

const SARVAM_TOOL_LABEL: Record<string, string> = {
  'ai-transcribe': 'AI Transcribe',
  'translate': 'Translate',
  'video-subtitles': 'Video Subtitles',
};

const OPENAI_TOOL_LABEL: Record<string, string> = {
  'ai-blog': 'Blog Writer',
  'ai-captions': 'Caption Generator',
  'ai-product': 'Product Copy',
  'ai-email': 'Email Copywriter',
  'ai-ad-copy': 'Ad Copy',
  'ai-ideas': 'Content Calendar / Ideas',
  'tone-shift': 'Tone Shift',
  'ai-hashtags': 'Hashtag Generator',
  'ai-script': 'Script Generator',
  'ig-audit': 'IG Audit (AI Strategy)',
  'ig-compare': 'IG Compare (AI Verdict)',
  'web-intel': 'Site Intelligence',
  'video-subtitles': 'Video Subtitles',
  'ai-performance-excel': 'AI Performance Excel',
  'knowledge-base-ingestion': 'Knowledge Base (system)',
};

const TOOL_ICON: Record<string, LucideIcon> = {
  'ai-transcribe': Mic,
  'translate': Languages,
  'video-subtitles': Captions,
};

function formatSarvamUsage(tool: SarvamToolBreakdown): string {
  if (tool.metricType === 'stt_seconds') return formatDuration(tool.totalMetricValue);
  return `${Math.round(tool.totalMetricValue).toLocaleString()} chars translated`;
}

function formatOpenAiUsage(tool: OpenAiToolBreakdown): string {
  if (tool.metricType === 'openai_audio_seconds') return `${formatDuration(tool.totalAudioSeconds)} audio`;
  const total = tool.totalInputTokens + tool.totalOutputTokens;
  return `${total.toLocaleString()} tokens (${tool.totalInputTokens.toLocaleString()} in / ${tool.totalOutputTokens.toLocaleString()} out)`;
}

function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function formatCredits(n: number): string {
  return `${n.toFixed(n < 10 ? 2 : 1)} cr`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M tokens`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K tokens`;
  return `${Math.round(n).toLocaleString()} tokens`;
}

function monthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

function userOpenAiTokens(u: UserUsageReport): number {
  return u.openaiTools
    .filter((t) => t.metricType === 'openai_tokens')
    .reduce((sum, t) => sum + t.totalInputTokens + t.totalOutputTokens, 0);
}

// ── Component ────────────────────────────────────────────────────────────────

const UsageReport: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1); // 1-12

  const [data, setData] = useState<UsageReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const monthParam = `${year}-${String(month).padStart(2, '0')}`;
  const isCurrentMonth = year === now.getUTCFullYear() && month === now.getUTCMonth() + 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/auth/admin/usage-report?month=${monthParam}`, { credentials: 'include' });
      if (res.status === 403) {
        setError('This report is only visible to admin accounts.');
        return;
      }
      if (!res.ok) {
        setError('Could not load the usage report.');
        return;
      }
      setData(await res.json());
    } catch {
      setError('Network error while loading the usage report.');
    } finally {
      setLoading(false);
    }
  }, [monthParam]);

  useEffect(() => { load(); }, [load]);

  const goPrevMonth = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); } else { setMonth((m) => m - 1); }
  };
  const goNextMonth = () => {
    if (isCurrentMonth) return;
    if (month === 12) { setYear((y) => y + 1); setMonth(1); } else { setMonth((m) => m + 1); }
  };

  const sarvamUsers = useMemo(
    () => (data?.users.filter((u) => u.totalSarvamCredits > 0) ?? []).sort((a, b) => b.totalSarvamCredits - a.totalSarvamCredits),
    [data]
  );
  const openaiUsers = useMemo(
    () => (data?.users.filter((u) => u.openaiTools.length > 0) ?? []).sort((a, b) => userOpenAiTokens(b) - userOpenAiTokens(a)),
    [data]
  );

  const sarvamCalls = useMemo(() => sarvamUsers.reduce((s, u) => s + u.sarvamTools.reduce((s2, t) => s2 + t.calls, 0), 0), [sarvamUsers]);
  const openaiCalls = useMemo(() => openaiUsers.reduce((s, u) => s + u.openaiTools.reduce((s2, t) => s2 + t.calls, 0), 0), [openaiUsers]);
  const openaiTotalTokens = useMemo(() => openaiUsers.reduce((s, u) => s + userOpenAiTokens(u), 0), [openaiUsers]);
  // The roster (every real account) vs. who actually did anything this month.
  const rosterCount = data?.users.length ?? 0;
  const activeUserCount = useMemo(() => data?.users.filter((u) => u.totalCalls > 0).length ?? 0, [data]);

  const maxSarvamCredits = useMemo(() => Math.max(1, ...sarvamUsers.map((u) => u.totalSarvamCredits)), [sarvamUsers]);
  const maxOpenAiTokens = useMemo(() => Math.max(1, ...openaiUsers.map(userOpenAiTokens)), [openaiUsers]);

  const sarvamDetailRows = useMemo(() => {
    return sarvamUsers.flatMap((u) =>
      u.sarvamTools.slice().sort((a, b) => b.creditsUsed - a.creditsUsed).map((t) => ({ username: u.username, ...t }))
    );
  }, [sarvamUsers]);

  const openaiDetailRows = useMemo(() => {
    return openaiUsers.flatMap((u) =>
      u.openaiTools.slice().sort((a, b) => (b.totalInputTokens + b.totalOutputTokens) - (a.totalInputTokens + a.totalOutputTokens)).map((t) => ({ username: u.username, ...t }))
    );
  }, [openaiUsers]);

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex p-3 bg-gradient-to-br from-[#e4c184] to-[#216974] rounded-2xl shadow-lg shadow-teal-200">
          <Coins size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">Usage &amp; Credits</h1>
        <p className="text-gray-500 text-lg max-w-xl mx-auto font-medium">
          Sarvam AI credits and OpenAI tokens used per person, tracked separately across every AI-powered tool.
        </p>
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={goPrevMonth}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors shadow-sm"
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="min-w-[180px] text-center text-sm font-bold text-gray-800 tracking-tight">
          {monthLabel(year, month)}
        </div>
        <button
          onClick={goNextMonth}
          disabled={isCurrentMonth}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors shadow-sm disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white"
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 size={28} className="animate-spin text-[#54c4b6]" />
          <span className="text-sm font-medium">Loading usage report…</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <Card className="border-0 shadow-xl">
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <div className="p-3 rounded-2xl bg-red-50">
              <AlertCircle size={24} className="text-red-500" />
            </div>
            <p className="text-gray-700 font-semibold">{error}</p>
          </div>
        </Card>
      )}

      {!loading && !error && data && (
        <>
          {/* Top-level comparison: Sarvam vs OpenAI */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="border-0 shadow-xl shadow-teal-100/60" accent="teal">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#e4c184] to-[#d4a855] text-white shrink-0">
                  <Coins size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Sarvam AI — credits used</div>
                  <div className="text-2xl font-black text-gray-900 truncate">{formatCredits(data.grandTotalSarvamCredits)}</div>
                  <div className="text-xs text-gray-400">{sarvamCalls.toLocaleString()} calls · {sarvamUsers.length} users</div>
                </div>
              </div>
            </Card>
            <Card className="border-0 shadow-xl shadow-teal-100/60" accent="violet">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#216974] to-[#54c4b6] text-white shrink-0">
                  <BrainCircuit size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">OpenAI — tokens used</div>
                  <div className="text-2xl font-black text-gray-900 truncate">{formatTokens(openaiTotalTokens)}</div>
                  <div className="text-xs text-gray-400">{openaiCalls.toLocaleString()} calls · {openaiUsers.length} users</div>
                </div>
              </div>
            </Card>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-400 font-medium">
            <Users size={14} /> {activeUserCount} of {rosterCount} team accounts used an AI tool this month
          </div>

          {/* Everyone — the full roster, including people with zero usage */}
          <Card title="Everyone" description="Every account, whether they used anything this month or not" noPadding className="border-0 shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 text-gray-400 text-xs uppercase tracking-wide">
                    <th className="text-left font-semibold px-6 py-3">User</th>
                    <th className="text-right font-semibold px-4 py-3">Sarvam credits</th>
                    <th className="text-right font-semibold px-4 py-3">OpenAI tokens</th>
                    <th className="text-right font-semibold px-6 py-3">Total calls</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.users.map((u) => {
                    const tokens = userOpenAiTokens(u);
                    const isIdle = u.totalCalls === 0;
                    return (
                      <tr key={u.username} className={`hover:bg-gray-50/60 transition-colors ${isIdle ? 'opacity-50' : ''}`}>
                        <td className="px-6 py-3 font-semibold text-gray-800 whitespace-nowrap">{u.username}</td>
                        <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{u.totalSarvamCredits > 0 ? formatCredits(u.totalSarvamCredits) : '—'}</td>
                        <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">{tokens > 0 ? formatTokens(tokens) : '—'}</td>
                        <td className="px-6 py-3 text-right font-bold text-gray-800">{u.totalCalls || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {activeUserCount === 0 ? (
            <Card className="border-0 shadow-xl">
              <div className="flex flex-col items-center justify-center py-14 gap-3 text-center">
                <div className="p-3 rounded-2xl bg-gray-50">
                  <Inbox size={24} className="text-gray-300" />
                </div>
                <p className="text-gray-500 font-medium">No AI usage recorded for {monthLabel(year, month)}.</p>
              </div>
            </Card>
          ) : (
            <>
              {/* ── Sarvam section ─────────────────────────────────────────── */}
              {sarvamUsers.length > 0 && (
                <>
                  <div className="flex items-center gap-2 pt-2">
                    <Coins size={16} className="text-[#e4c184]" />
                    <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Sarvam AI</h2>
                  </div>

                  <Card title="Credits used per user" description="Sorted highest to lowest" className="border-0 shadow-xl">
                    <div className="space-y-3">
                      {sarvamUsers.map((u) => (
                        <div key={u.username} className="flex items-center gap-3">
                          <div className="w-44 shrink-0 text-sm font-semibold text-gray-700 truncate" title={u.username}>
                            {u.username}
                          </div>
                          <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.max(3, (u.totalSarvamCredits / maxSarvamCredits) * 100)}%` }}
                              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                              className="h-full rounded-full bg-gradient-to-r from-[#54c4b6] to-[#216974]"
                            />
                          </div>
                          <div className="w-20 shrink-0 text-right text-sm font-bold text-gray-800">
                            {formatCredits(u.totalSarvamCredits)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>

                  <Card title="Breakdown by tool" description="What each user's Sarvam credits went toward" noPadding className="border-0 shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50/80 text-gray-400 text-xs uppercase tracking-wide">
                            <th className="text-left font-semibold px-6 py-3">User</th>
                            <th className="text-left font-semibold px-4 py-3">Tool</th>
                            <th className="text-right font-semibold px-4 py-3">Calls</th>
                            <th className="text-left font-semibold px-4 py-3">Usage</th>
                            <th className="text-right font-semibold px-6 py-3">Credits</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sarvamDetailRows.map((row, i) => {
                            const Icon = TOOL_ICON[row.toolId] || Coins;
                            return (
                              <tr key={`sv-${row.username}-${row.toolId}-${i}`} className="hover:bg-gray-50/60 transition-colors">
                                <td className="px-6 py-3 font-semibold text-gray-800 whitespace-nowrap">{row.username}</td>
                                <td className="px-4 py-3">
                                  <span className="inline-flex items-center gap-1.5 text-gray-600">
                                    <Icon size={14} className="text-[#54c4b6]" />
                                    {SARVAM_TOOL_LABEL[row.toolId] || row.toolId}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-500">{row.calls}</td>
                                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatSarvamUsage(row)}</td>
                                <td className="px-6 py-3 text-right font-bold text-gray-800">{formatCredits(row.creditsUsed)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </>
              )}

              {/* ── OpenAI section ─────────────────────────────────────────── */}
              {openaiUsers.length > 0 && (
                <>
                  <div className="flex items-center gap-2 pt-2">
                    <BrainCircuit size={16} className="text-[#216974]" />
                    <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">OpenAI</h2>
                  </div>

                  <Card title="Tokens used per user" description="Sorted highest to lowest — text/embedding calls only" className="border-0 shadow-xl">
                    <div className="space-y-3">
                      {openaiUsers.map((u) => {
                        const tokens = userOpenAiTokens(u);
                        return (
                          <div key={u.username} className="flex items-center gap-3">
                            <div className="w-44 shrink-0 text-sm font-semibold text-gray-700 truncate" title={u.username}>
                              {u.username}
                            </div>
                            <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.max(3, (tokens / maxOpenAiTokens) * 100)}%` }}
                                transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                                className="h-full rounded-full bg-gradient-to-r from-[#216974] to-[#54c4b6]"
                              />
                            </div>
                            <div className="w-28 shrink-0 text-right text-sm font-bold text-gray-800">
                              {formatTokens(tokens)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>

                  <Card title="Breakdown by tool" description="What each user's OpenAI usage went toward" noPadding className="border-0 shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50/80 text-gray-400 text-xs uppercase tracking-wide">
                            <th className="text-left font-semibold px-6 py-3">User</th>
                            <th className="text-left font-semibold px-4 py-3">Tool</th>
                            <th className="text-left font-semibold px-4 py-3">Model</th>
                            <th className="text-right font-semibold px-4 py-3">Calls</th>
                            <th className="text-left font-semibold px-6 py-3">Usage</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {openaiDetailRows.map((row, i) => (
                            <tr key={`oa-${row.username}-${row.toolId}-${row.model}-${i}`} className="hover:bg-gray-50/60 transition-colors">
                              <td className="px-6 py-3 font-semibold text-gray-800 whitespace-nowrap">{row.username}</td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1.5 text-gray-600">
                                  <Sparkles size={14} className="text-[#216974]" />
                                  {OPENAI_TOOL_LABEL[row.toolId] || row.toolId}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{row.model || '—'}</td>
                              <td className="px-4 py-3 text-right text-gray-500">{row.calls}</td>
                              <td className="px-6 py-3 text-gray-700 whitespace-nowrap">{formatOpenAiUsage(row)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </>
              )}
            </>
          )}

          {/* Pricing footnote — deliberately no currency shown, just where rates live */}
          <p className="text-center text-xs text-gray-400 font-medium">
            Sarvam rates: {data.pricing.sarvam.sttInrPerHour} credits/hour of audio · {data.pricing.sarvam.translateInrPer10kChars} credits per 10,000 characters —{' '}
            <a href={data.pricing.sarvam.source} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[#216974] hover:text-[#54c4b6] font-semibold">
              Sarvam pricing <ExternalLink size={11} />
            </a>
            {' '}· OpenAI usage shown in tokens —{' '}
            <a href={data.pricing.openai.source} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[#216974] hover:text-[#54c4b6] font-semibold">
              OpenAI pricing <ExternalLink size={11} />
            </a>
            . Tracking started Sept 1, 2026 — earlier months aren't available.
          </p>
        </>
      )}
    </div>
  );
};

export default UsageReport;
